import React, { ReactElement } from "react";
import { Layer, Rect } from "react-konva";

import { Color, getResizeHandlerPositionsForZone, isTableLabel } from "../helpers";
import { CurrentAction, State } from "../StateManager/state";
import { Zone } from "../types";
import { Offset, Scale } from "./types";
import { Zone as ZoneComponent } from "./Zone";
import { ZoneResizeControl } from "./ZoneResizeControl";
import { ZoneTag } from "./ZoneTag";

type Props = Readonly<{
  activeZones: ReadonlyArray<number>;
  currentAction: CurrentAction;
  hoveredZone: number;
  offset: Offset;
  pageHeight: number;
  scale: Scale;
  taxonomy: State["taxonomy"];
  zones: ReadonlyArray<Zone>;
  showZoneLabels: boolean;
}>;

const positions = ["top", "right", "bottom", "left"];
const UnknownStroke = Color.fromString("rgba(0.0, 0.0, 0.0, 0.5)");

export const Zones = ({
  activeZones,
  currentAction,
  hoveredZone,
  offset,
  pageHeight,
  scale,
  taxonomy,
  zones,
  showZoneLabels
}: Props) => {

  if (isNaN(offset.x) || isNaN(offset.y) || isNaN(scale.x) || isNaN(scale.y)) {
    return <Layer />;
  }
  const showResizeHandlers = !(
    currentAction === CurrentAction.RESIZING_BOTTOM ||
    currentAction === CurrentAction.RESIZING_LEFT ||
    currentAction === CurrentAction.RESIZING_RIGHT ||
    currentAction === CurrentAction.RESIZING_TOP
  );
  const tagPosition =
    zones[hoveredZone] && zones[hoveredZone].maxY > pageHeight * 0.8 ? "above" : "below";
  return (
    <Layer>
      {zones.map((z, index) => {
        let cells: ReactElement | undefined;
        if (isTableLabel(z.label, taxonomy) && Array.isArray(z.cells)) {

          let cellStrokeColor = UnknownStroke;
          if (taxonomy.rawLabels[z.label] && taxonomy.rawLabels[z.label].color) {
            cellStrokeColor = taxonomy.rawLabels[z.label].color.copy();
            cellStrokeColor.alpha = 0.5;
          }

          let stroke = cellStrokeColor.toRGBAString();
          
          cells = (
            <>
              {z.cells.map((c, cellIndex) => (
                <Rect
                  height={(c.maxY - c.minY) * scale.y}
                  key={`zone-${index}-cell-${cellIndex}`}
                  stroke={stroke}
                  width={(c.maxX - c.minX) * scale.x}
                  x={c.minX * scale.x + offset.x}
                  y={c.minY * scale.y + offset.y}
                />
              ))}
            </>
          );
        }
        return (
          <ZoneComponent
            cells={cells}
            key={`zone-${index}`}
            label={z.label}
            point={{
              x: z.minX * scale.x + offset.x,
              y: z.minY * scale.y + offset.y,
            }}
            size={{
              height: (z.maxY - z.minY) * scale.y,
              width: (z.maxX - z.minX) * scale.x,
            }}
            taxonomy={taxonomy}
            showZoneLabels={showZoneLabels}
          />
        );
      })}
      {showResizeHandlers &&
        activeZones.map(z =>
          getResizeHandlerPositionsForZone(zones[z]).map((center, position) => (
            <ZoneResizeControl
              center={{
                x: center.x * scale.x + offset.x,
                y: center.y * scale.y + offset.y,
              }}
              key={`resize-${z}-${positions[position]}`}
            />
          )),
        )}
      <ZoneTag
        hoveredZone={hoveredZone}
        key={`hovering-${hoveredZone}`}
        offset={offset}
        position={tagPosition}
        scale={scale}
        zones={zones}
      />
    </Layer>
  );
};
